import { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@shared/design-system/ui'
import {
  confirmActions,
  confirmBackdrop,
  confirmDescription,
  confirmNote,
  confirmPanel,
  confirmTitle,
} from './members-area.css'

export function DeleteConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  pendingLabel = 'Excluindo...',
  pending = false,
  onCancel,
  onConfirm,
}: {
  open: boolean
  title: string
  description: string
  confirmLabel: string
  pendingLabel?: string
  pending?: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const titleId = useId()
  const panelRef = useRef<HTMLElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const pendingRef = useRef(pending)
  const onCancelRef = useRef(onCancel)

  useEffect(() => {
    pendingRef.current = pending
  }, [pending])

  useEffect(() => {
    onCancelRef.current = onCancel
  }, [onCancel])

  useEffect(() => {
    if (!open) return
    const previousActive =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    cancelRef.current?.focus()

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        if (!pendingRef.current) onCancelRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      )
      const first = focusable[0]
      const last = focusable.at(-1)
      if (!first || !last) return
      const active = document.activeElement
      if (!panelRef.current?.contains(active)) {
        event.preventDefault()
        first.focus()
        return
      }
      if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      previousActive?.focus()
    }
  }, [open])

  if (!open) return null

  return createPortal(
    <div
      className={confirmBackdrop}
      role="presentation"
      onClick={() => {
        if (!pending) onCancel()
      }}
    >
      <section
        ref={panelRef}
        className={confirmPanel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <div>
          <h2 id={titleId} className={confirmTitle}>
            {title}
          </h2>
        </div>
        <p className={confirmDescription}>{description}</p>
        <p className={confirmNote}>Esta ação não pode ser desfeita.</p>
        <div className={confirmActions}>
          <Button ref={cancelRef} variant="secondary" disabled={pending} onClick={onCancel}>
            Cancelar
          </Button>
          <Button variant="danger" disabled={pending} onClick={onConfirm}>
            {pending ? pendingLabel : confirmLabel}
          </Button>
        </div>
      </section>
    </div>,
    document.body,
  )
}
